import React, { useMemo, useState, useEffect } from "react";
import { Calculator, Check, ChevronRight, Sparkles } from "lucide-react";
import { PRODUCTS } from "../lib/products";
import { formatAmount as fmt, pickAmount } from "../lib/currency";

// Sensible default quantities per pricing unit. Users adjust per row.
const DEFAULT_QTY = {
    workspace: 1,
    employee: 25,
    project: 1,
    sender: 1,
};

const UNIT_BOUNDS = {
    workspace: { min: 1, max: 10, step: 1 },
    employee: { min: 1, max: 500, step: 1 },
    project: { min: 1, max: 20, step: 1 },
    sender: { min: 1, max: 20, step: 1 },
};

const UNIT_LABEL = {
    workspace: "workspace",
    employee: "employees",
    project: "projects",
    sender: "senders",
};

export const PricingCalculator = ({
    pricing,
    currency,
    bundleDiscountPct,
    annualDiscountPct = 20,
    initialAnnual = true,
}) => {
    // Selected slugs (set of strings)
    const [selected, setSelected] = useState(() => new Set());
    // qty by slug
    const [qty, setQty] = useState({});
    // billing cadence: defaults to annual (per pricing framework)
    const [annual, setAnnual] = useState(initialAnnual);

    // Keep in sync if parent's billing toggle changes.
    useEffect(() => {
        setAnnual(initialAnnual);
    }, [initialAnnual]);

    const toggle = (slug) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(slug)) {
                next.delete(slug);
            } else {
                next.add(slug);
                // initialize qty to default
                const unit = pricing[slug]?.unit;
                setQty((q) => ({
                    ...q,
                    [slug]: q[slug] ?? DEFAULT_QTY[unit] ?? 1,
                }));
            }
            return next;
        });
    };

    const setQuantity = (slug, value) => {
        const unit = pricing[slug]?.unit;
        const bounds = UNIT_BOUNDS[unit] || { min: 1, max: 999 };
        const n = Math.max(bounds.min, Math.min(bounds.max, Number(value) || 1));
        setQty((q) => ({ ...q, [slug]: n }));
    };

    const rows = useMemo(() => {
        return PRODUCTS.map((p) => {
            const price = pricing[p.slug];
            if (!price) return null;
            const isSelected = selected.has(p.slug);
            const quantity = qty[p.slug] ?? DEFAULT_QTY[price.unit] ?? 1;
            // Per-currency monthly rate (NOT annual — annual handled separately)
            const monthlyRate = pickAmount(price.monthly, currency);
            const baseLine = monthlyRate * quantity;
            return {
                slug: p.slug,
                name: p.name,
                sub: p.sub,
                logo: p.logo,
                unit: price.unit,
                unitLabel: UNIT_LABEL[price.unit] || price.unit,
                monthlyRate,
                selected: isSelected,
                quantity,
                baseLine,
            };
        }).filter(Boolean);
    }, [pricing, selected, qty, currency]);

    const summary = useMemo(() => {
        const chosen = rows.filter((r) => r.selected);
        const subtotal = chosen.reduce((sum, r) => sum + r.baseLine, 0);
        const bundleEligible = chosen.length >= 2;
        const bundleSaving = bundleEligible
            ? Math.round((subtotal * bundleDiscountPct) / 100)
            : 0;
        const afterBundle = subtotal - bundleSaving;
        const annualSaving = annual
            ? Math.round((afterBundle * annualDiscountPct) / 100)
            : 0;
        const total = afterBundle - annualSaving;
        // Effective combined % off the original subtotal
        const effectivePct =
            subtotal > 0 ? Math.round(((subtotal - total) / subtotal) * 100) : 0;
        return {
            chosen,
            count: chosen.length,
            subtotal,
            bundleEligible,
            bundleSaving,
            afterBundle,
            annual,
            annualSaving,
            total,
            effectivePct,
            periodTotal: annual ? total * 12 : total,
        };
    }, [rows, bundleDiscountPct, annualDiscountPct, annual]);

    const continueHref = useMemo(() => {
        if (!summary.chosen.length) return "/contact";
        const slugs = summary.chosen.map((r) => r.slug).join(",");
        return `/contact?products=${encodeURIComponent(slugs)}`;
    }, [summary.chosen]);

    return (
        <section
            className="section"
            data-testid="pricing-calculator"
            aria-labelledby="pricing-calc-h2"
            style={{ paddingTop: 24 }}
        >
            <div className="container">
                <div className="section-head" style={{ textAlign: "center" }}>
                    <span className="eyebrow">Build your bundle</span>
                    <h2
                        id="pricing-calc-h2"
                        className="h2"
                        style={{ marginTop: 16 }}
                    >
                        Estimate your bill in 30 seconds.
                    </h2>
                    <p
                        className="muted"
                        style={{ maxWidth: 580, margin: "10px auto 0" }}
                    >
                        Pick the products you actually need, set your size, and the
                        calculator applies the {bundleDiscountPct}% bundle discount
                        automatically.
                    </p>
                </div>

                <div className="calc-grid">
                    {/* Product picker */}
                    <div className="calc-products" data-testid="calc-products">
                        {rows.map((r) => {
                            const bounds = UNIT_BOUNDS[r.unit] || {
                                min: 1,
                                max: 100,
                                step: 1,
                            };
                            return (
                                <div
                                    key={r.slug}
                                    className={`calc-product-row ${
                                        r.selected ? "is-on" : ""
                                    }`}
                                    data-testid={`calc-row-${r.slug}`}
                                >
                                    <button
                                        type="button"
                                        className="calc-product-toggle"
                                        onClick={() => toggle(r.slug)}
                                        aria-pressed={r.selected}
                                        data-testid={`calc-toggle-${r.slug}`}
                                    >
                                        <span
                                            className={`calc-checkbox ${
                                                r.selected ? "on" : ""
                                            }`}
                                            aria-hidden="true"
                                        >
                                            {r.selected && <Check size={14} />}
                                        </span>
                                        <span className="calc-product-info">
                                            <span className="calc-product-name">
                                                {r.name}
                                            </span>
                                            <span className="calc-product-sub">
                                                {r.sub}
                                            </span>
                                        </span>
                                        <span className="calc-product-price">
                                            {fmt(r.monthlyRate, currency)}
                                            <small>/{r.unit}</small>
                                        </span>
                                    </button>

                                    {r.selected && (
                                        <div
                                            className="calc-qty"
                                            data-testid={`calc-qty-${r.slug}`}
                                        >
                                            <label
                                                htmlFor={`qty-${r.slug}`}
                                                className="calc-qty-label"
                                            >
                                                {r.unit === "workspace"
                                                    ? "Workspaces"
                                                    : `${
                                                          r.unitLabel.charAt(0).toUpperCase() +
                                                          r.unitLabel.slice(1)
                                                      }`}
                                            </label>
                                            <div className="calc-qty-controls">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setQuantity(
                                                            r.slug,
                                                            r.quantity - 1
                                                        )
                                                    }
                                                    aria-label="Decrease"
                                                    data-testid={`calc-qty-dec-${r.slug}`}
                                                    disabled={r.quantity <= bounds.min}
                                                >
                                                    −
                                                </button>
                                                <input
                                                    id={`qty-${r.slug}`}
                                                    type="number"
                                                    min={bounds.min}
                                                    max={bounds.max}
                                                    step={bounds.step}
                                                    value={r.quantity}
                                                    onChange={(e) =>
                                                        setQuantity(
                                                            r.slug,
                                                            e.target.value
                                                        )
                                                    }
                                                    data-testid={`calc-qty-input-${r.slug}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setQuantity(
                                                            r.slug,
                                                            r.quantity + 1
                                                        )
                                                    }
                                                    aria-label="Increase"
                                                    data-testid={`calc-qty-inc-${r.slug}`}
                                                    disabled={r.quantity >= bounds.max}
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <span className="calc-line-total">
                                                = {fmt(r.baseLine, currency)}
                                                <small>/mo</small>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Live summary */}
                    <aside
                        className="calc-summary"
                        data-testid="calc-summary"
                        aria-live="polite"
                    >
                        <div className="calc-summary-head">
                            <Calculator size={18} />
                            <span>Your estimate</span>
                        </div>

                        {/* Annual toggle */}
                        <div
                            className="calc-billing"
                            role="group"
                            aria-label="Billing cadence"
                        >
                            <button
                                type="button"
                                className={!annual ? "active" : ""}
                                onClick={() => setAnnual(false)}
                                data-testid="calc-billing-monthly"
                            >
                                Monthly
                            </button>
                            <button
                                type="button"
                                className={annual ? "active" : ""}
                                onClick={() => setAnnual(true)}
                                data-testid="calc-billing-annual"
                            >
                                Annual <span className="calc-save-pill">−20%</span>
                            </button>
                        </div>

                        {!summary.count ? (
                            <p
                                className="muted"
                                style={{
                                    margin: "24px 0",
                                    textAlign: "center",
                                    fontSize: "0.92rem",
                                }}
                            >
                                Pick at least one product to see your estimate.
                            </p>
                        ) : (
                            <>
                                <ul className="calc-line-items">
                                    {summary.chosen.map((r) => (
                                        <li key={r.slug}>
                                            <span>
                                                {r.name} × {r.quantity}
                                            </span>
                                            <span>{fmt(r.baseLine, currency)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="calc-divider" />
                                <ul className="calc-totals">
                                    <li>
                                        <span>Subtotal</span>
                                        <span>{fmt(summary.subtotal, currency)}</span>
                                    </li>
                                    {summary.bundleEligible && (
                                        <li
                                            className="calc-discount-row"
                                            data-testid="calc-bundle-row"
                                        >
                                            <span>
                                                <Sparkles size={13} /> Bundle (
                                                {bundleDiscountPct}% off)
                                            </span>
                                            <span>
                                                − {fmt(summary.bundleSaving, currency)}
                                            </span>
                                        </li>
                                    )}
                                    {annual && (
                                        <li
                                            className="calc-discount-row"
                                            data-testid="calc-annual-row"
                                        >
                                            <span>Annual prepay (20% off)</span>
                                            <span>
                                                − {fmt(summary.annualSaving, currency)}
                                            </span>
                                        </li>
                                    )}
                                </ul>
                                <div
                                    className="calc-grand-total"
                                    data-testid="calc-grand-total"
                                >
                                    <span>{annual ? "Per month" : "Total"}</span>
                                    <span>
                                        <strong>{fmt(summary.total, currency)}</strong>
                                        <small>/mo</small>
                                    </span>
                                </div>
                                {summary.effectivePct > 0 && summary.bundleEligible && annual && (
                                    <div
                                        className="calc-combined-badge"
                                        data-testid="calc-combined-badge"
                                    >
                                        Save up to <strong>{summary.effectivePct}%</strong> · Bundle + Annual stacked
                                    </div>
                                )}
                                {annual && (
                                    <p
                                        className="muted"
                                        style={{
                                            fontSize: "0.78rem",
                                            margin: "6px 0 0",
                                            textAlign: "right",
                                        }}
                                    >
                                        Billed annually: {fmt(summary.periodTotal, currency)}
                                    </p>
                                )}
                                {!summary.bundleEligible && summary.count === 1 && (
                                    <p
                                        className="calc-tip"
                                        data-testid="calc-bundle-tip"
                                    >
                                        💡 Add one more product and the entire account
                                        drops {bundleDiscountPct}%.
                                    </p>
                                )}
                                <a
                                    href={continueHref}
                                    className="btn btn-primary btn-lg"
                                    style={{ width: "100%", marginTop: 16 }}
                                    data-testid="calc-cta-continue"
                                >
                                    Talk to us about this bundle
                                    <ChevronRight size={16} />
                                </a>
                                <p
                                    className="muted"
                                    style={{
                                        fontSize: "0.74rem",
                                        margin: "10px 0 0",
                                        textAlign: "center",
                                    }}
                                >
                                    Indicative price. Final quote includes setup, data migration, and rollout.
                                </p>
                            </>
                        )}
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default PricingCalculator;
